import { FormlyFieldConfig } from '@ngx-formly/core';

import { clearableIsReadOnly } from '../clearable-field.type';

/** Marks a read-only field so the field-state tokens can recess it (#20818). */
export const READONLY_FIELD_CLASS = 'dd-field-readonly';

/**
 * Stamps `dd-field-readonly` on read-only fields so the read-only fill in
 * `styles/components/_forms.scss` reaches every field family.
 *
 * The stylesheet already recognises a native `[readonly]` on the control, which covers the
 * text-shaped families. It cannot see the ones that model read-only in TypeScript instead —
 * `mat-select`, the pickers, the composite fields — and those are exactly the fields where a
 * read-only value is most likely to be mistaken for an editable one. The class is set on
 * `field.className` (the `<formly-field>` element) rather than on the `<mat-form-field>`, which
 * Formly does not expose: `--dd-field-bg` inherits, so an ancestor is enough for the token
 * remap to land on the wrapper.
 *
 * Set through `expressions` rather than assigned once, because `props.readonly` is routinely
 * expression-driven (a field that unlocks after another is filled). A static className would
 * freeze the first evaluation.
 */
export function readonlyFieldExtension(field: FormlyFieldConfig): void {
  if (!field.props || field.fieldGroup || field.key === undefined || field.key === null) {
    return;
  }

  // A configured className expression is authoritative — it may already compose classes of its
  // own, and overwriting it would drop them.
  if (field.expressions?.['className']) {
    return;
  }

  const baseClassName = field.className ?? '';
  if (baseClassName.split(/\s+/).includes(READONLY_FIELD_CLASS)) {
    return;
  }

  field.expressions = {
    ...field.expressions,
    className: () =>
      clearableIsReadOnly(field.props) ? `${baseClassName} ${READONLY_FIELD_CLASS}`.trim() : baseClassName,
  };
}
